import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";

const OrderStatus = () => {
  const { orderId, vendorId } = useParams();
  const navigate = useNavigate();
  const [orderStatus, setOrderStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  useEffect(() => {
    if (!orderId || !vendorId) {
      setError("Missing order or vendor information");
      setLoading(false);
      return;
    }

    const fetchOrderStatus = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          setError("Authentication required");
          setLoading(false);
          return;
        }

        // ✅ Fetch order status using the correct endpoint
        const response = await axios.get(
          `https://backend.maamaas.com/food/api/cart/get/ordertype=TABLE_DINE_IN/${vendorId}/CONFIRMED`,
          {
            headers: {
              "Authorization": `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.status === 200) {
          setOrderStatus(response.data);
          setLastUpdated(new Date());
          console.log("Order Status:", response.data);
        }
      } catch (err) {
        console.error("❌ Failed to fetch order status:", err.response?.data || err.message);
        setError(err.response?.data?.message || "Failed to load order status");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderStatus();

    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchOrderStatus, 10000);
    return () => clearInterval(interval);
  }, [orderId, vendorId]);

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "CONFIRMED":
        return "#28a745";
      case "PENDING":
        return "#ffc107";
      case "PREPARING":
        return "#17a2b8";
      case "READY":
        return "#007bff";
      case "COMPLETED":
        return "#6c757d";
      case "CANCELLED":
        return "#dc3545";
      default:
        return "#6c757d";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case "CONFIRMED":
        return "✅";
      case "PENDING":
        return "⏳";
      case "PREPARING":
        return "👨‍🍳";
      case "READY":
        return "🍽️";
      case "COMPLETED":
        return "🎉";
      case "CANCELLED":
        return "❌";
      default:
        return "📋";
    }
  };

  const getStatusMessage = (status) => {
    switch (status?.toUpperCase()) {
      case "CONFIRMED":
        return "Your order has been confirmed!";
      case "PENDING":
        return "Your order is pending confirmation.";
      case "PREPARING":
        return "Your order is being prepared by our chefs!";
      case "READY":
        return "Your order is ready for pickup/delivery!";
      case "COMPLETED":
        return "Your order has been completed. Enjoy your meal!";
      case "CANCELLED":
        return "Your order has been cancelled.";
      default:
        return "Processing your order...";
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Loading order status...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorCard}>
          <h3 style={styles.errorTitle}>Error Loading Order Status</h3>
          <p style={styles.errorMessage}>{error}</p>
          <button onClick={() => navigate(-1)} style={styles.backBtn}>Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.headerTitle}>Order Status</h2>
        <button onClick={() => navigate(-1)} style={styles.closeBtn}>✕</button>
      </div>

      <div style={styles.content}>
        {/* Order Summary Card */}
        <div style={styles.summaryCard}>
          <div style={styles.orderIdBadge}>
            <span style={styles.orderIdLabel}>Order ID: </span>
            <strong style={styles.orderIdValue}>{orderId}</strong>
          </div>
          
          <div style={{...styles.statusIndicator, borderColor: getStatusColor(orderStatus?.status)}}>
            <div style={styles.statusIcon}>{getStatusIcon(orderStatus?.status)}</div>
            <div style={styles.statusText}>
              <span style={styles.statusLabel}>Current Status</span>
              <span style={{...styles.statusValue, color: getStatusColor(orderStatus?.status)}}>
                {orderStatus?.status || "PROCESSING"}
              </span>
            </div>
          </div>
          
          <p style={styles.statusMessage}>{getStatusMessage(orderStatus?.status)}</p>
        </div>

        {/* Timeline */}
        <div style={styles.timelineSection}>
          <h3 style={styles.sectionTitle}>Order Progress</h3>
          <div style={styles.timelineSteps}>
            <div style={{...styles.timelineStep, ...((orderStatus?.status === "CONFIRMED" || orderStatus?.status === "PREPARING" || orderStatus?.status === "READY" || orderStatus?.status === "COMPLETED") ? styles.completed : {})}}>
              <div style={styles.stepCircle}>1</div>
              <div style={styles.stepLabel}>Order Confirmed</div>
              <div style={styles.stepTime}>{orderStatus?.confirmedTime || "Waiting"}</div>
            </div>
            
            <div style={{...styles.timelineStep, ...((orderStatus?.status === "PREPARING" || orderStatus?.status === "READY" || orderStatus?.status === "COMPLETED") ? styles.completed : {})}}>
              <div style={styles.stepCircle}>2</div>
              <div style={styles.stepLabel}>Preparing</div>
              <div style={styles.stepTime}>{orderStatus?.preparingTime || "Waiting"}</div>
            </div>
            
            <div style={{...styles.timelineStep, ...((orderStatus?.status === "READY" || orderStatus?.status === "COMPLETED") ? styles.completed : {})}}>
              <div style={styles.stepCircle}>3</div>
              <div style={styles.stepLabel}>Ready for Pickup</div>
              <div style={styles.stepTime}>{orderStatus?.readyTime || "Waiting"}</div>
            </div>
            
            <div style={{...styles.timelineStep, ...((orderStatus?.status === "COMPLETED") ? styles.completed : {})}}>
              <div style={styles.stepCircle}>4</div>
              <div style={styles.stepLabel}>Order Completed</div>
              <div style={styles.stepTime}>{orderStatus?.completedTime || "Waiting"}</div>
            </div>
          </div>
        </div>

        {/* Order Items */}
        {orderStatus?.items && orderStatus.items.length > 0 && (
          <div style={styles.itemsSection}>
            <h3 style={styles.sectionTitle}>Order Items</h3>
            <table style={styles.itemsTable}>
              <thead>
                <tr style={styles.tableHeader}>
                  <th style={styles.tableHeaderCell}>Item</th>
                  <th style={styles.tableHeaderCell}>Quantity</th>
                  <th style={styles.tableHeaderCell}>Status</th>
                </tr>
              </thead>
              <tbody>
                {orderStatus.items.map((item, index) => (
                  <tr key={index} style={styles.tableRow}>
                    <td style={styles.tableCell}>{item.dishName || item.name}</td>
                    <td style={styles.tableCell}>{item.quantity}</td>
                    <td style={styles.tableCell}>
                      <span style={{...styles.itemStatusBadge, backgroundColor: getStatusColor(item.status)}}>
                        {item.status || "Confirmed"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Last Updated */}
        <div style={styles.lastUpdated}>
          Last updated: {lastUpdated.toLocaleTimeString()}
        </div>

        {/* Action Buttons */}
        <div style={styles.actionButtons}>
          <button onClick={() => window.location.reload()} style={styles.refreshBtn}>
            🔄 Refresh Status
          </button>
          <button onClick={() => navigate("/")} style={styles.homeBtn}>
            🏠 Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: "800px",
    margin: "20px auto",
    background: "#fff",
    borderRadius: "16px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
  },
  header: {
    background: "#ee6521",
    color: "white",
    padding: "16px 20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerTitle: {
    margin: 0,
    fontSize: "20px",
  },
  closeBtn: {
    background: "transparent",
    border: "none",
    color: "white",
    fontSize: "24px",
    cursor: "pointer",
    padding: 0,
    width: "30px",
    height: "30px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
  },
  content: {
    padding: "20px",
  },
  summaryCard: {
    background: "#f8f9fa",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "20px",
  },
  orderIdBadge: {
    fontSize: "16px",
    marginBottom: "15px",
    paddingBottom: "15px",
    borderBottom: "1px solid #e0e0e0",
  },
  orderIdLabel: {
    color: "#666",
  },
  orderIdValue: {
    color: "#333",
  },
  statusIndicator: {
    display: "flex",
    alignItems: "center",
    gap: "15px",
    padding: "15px",
    border: "2px solid",
    borderRadius: "12px",
    background: "white",
    marginBottom: "15px",
  },
  statusIcon: {
    fontSize: "40px",
  },
  statusText: {
    display: "flex",
    flexDirection: "column",
  },
  statusLabel: {
    fontSize: "12px",
    color: "#666",
  },
  statusValue: {
    fontSize: "24px",
    fontWeight: "bold",
  },
  statusMessage: {
    fontSize: "14px",
    color: "#666",
    margin: "10px 0 0 0",
    textAlign: "center",
  },
  timelineSection: {
    marginBottom: "20px",
  },
  sectionTitle: {
    marginBottom: "15px",
    color: "#333",
    fontSize: "18px",
  },
  timelineSteps: {
    display: "flex",
    justifyContent: "space-between",
    position: "relative",
  },
  timelineStep: {
    flex: 1,
    textAlign: "center",
    position: "relative",
    zIndex: 2,
    background: "white",
  },
  stepCircle: {
    width: "40px",
    height: "40px",
    background: "#e0e0e0",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 10px",
    fontWeight: "bold",
    color: "#666",
  },
  completed: {
    stepCircle: {
      background: "#28a745",
      color: "white",
    },
  },
  stepLabel: {
    fontSize: "12px",
    fontWeight: "500",
    marginBottom: "5px",
  },
  stepTime: {
    fontSize: "10px",
    color: "#999",
  },
  itemsSection: {
    marginBottom: "20px",
    overflowX: "auto",
  },
  itemsTable: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeader: {
    background: "#f8f9fa",
  },
  tableHeaderCell: {
    padding: "10px",
    textAlign: "left",
    fontWeight: "600",
    borderBottom: "1px solid #e0e0e0",
  },
  tableRow: {
    borderBottom: "1px solid #e0e0e0",
  },
  tableCell: {
    padding: "10px",
    textAlign: "left",
  },
  itemStatusBadge: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: "12px",
    fontSize: "12px",
    color: "white",
  },
  lastUpdated: {
    textAlign: "center",
    fontSize: "11px",
    color: "#999",
    marginBottom: "15px",
  },
  actionButtons: {
    display: "flex",
    gap: "10px",
    justifyContent: "center",
    marginTop: "20px",
  },
  refreshBtn: {
    padding: "10px 20px",
    background: "#ee6521",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer",
  },
  homeBtn: {
    padding: "10px 20px",
    background: "#6c757d",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
  },
  spinner: {
    width: "50px",
    height: "50px",
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #ee6521",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  loadingText: {
    marginTop: "20px",
    color: "#666",
  },
  errorContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
  },
  errorCard: {
    background: "#fff",
    padding: "30px",
    borderRadius: "12px",
    textAlign: "center",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
  },
  errorTitle: {
    color: "#dc3545",
    marginBottom: "10px",
  },
  errorMessage: {
    color: "#666",
    marginBottom: "20px",
  },
  backBtn: {
    padding: "10px 20px",
    background: "#6c757d",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
};

// Add keyframes for spinner animation
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @media (max-width: 768px) {
    .timeline-steps {
      flex-direction: column;
      gap: 15px;
    }
  }
`;
document.head.appendChild(styleSheet);

export default OrderStatus;